'use client';

import { type MouseEventHandler, forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { UpsellBannerRootContext } from './context/upsell-banner-root.context';
import { upsellBannerRootRecipe } from './styles/upsell-banner-root.styles';
import type { UpsellBannerRootPropsInterface } from './types/upsell-banner-root.types';

/**
 * @since 10.0.0
 */
export const UpsellBannerRoot = forwardRef<HTMLDivElement, UpsellBannerRootPropsInterface>(
  (props: UpsellBannerRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      className,
      css: cssProp,
      open: controlledOpen,
      onOpenChange: setControlledOpen,
      tone = 'soft',
      ...rest
    } = props;

    const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean | undefined>(
      typeof controlledOpen === 'boolean' ? undefined : true,
    );
    const open = typeof controlledOpen === 'boolean' ? controlledOpen : uncontrolledOpen;

    const onOpenChange = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        if (typeof controlledOpen === 'boolean') {
          setControlledOpen?.(false, event);
        } else {
          setUncontrolledOpen(false);
          setControlledOpen?.(false, event);
        }
      },
      [controlledOpen, setControlledOpen],
    );

    const styles = css(upsellBannerRootRecipe.raw({ tone }), cssProp);

    const Component = asChild ? Slot : 'div';

    const contextValue = useMemo(() => ({ open, onOpenChange, tone }), [open, onOpenChange, tone]);

    return (
      <UpsellBannerRootContext.Provider value={contextValue}>
        {open ? (
          <Component
            ref={forwardedRef}
            data-c11n-component="UpsellBanner.Root"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', 'c11n_upsell-banner__root', styles, className)}
            {...rest}
          />
        ) : null}
      </UpsellBannerRootContext.Provider>
    );
  },
);

UpsellBannerRoot.displayName = 'UpsellBanner.Root';
